import { useState, useEffect, useCallback, useRef } from "react";

interface UseRotatingWordsOptions {
  words: string[];
  interval?: number;
  animationDuration?: number;
}

type AnimationPhase = "visible" | "exiting" | "entering";

export const useRotatingWords = ({ 
  words, 
  interval = 3000, 
  animationDuration = 500 
}: UseRotatingWordsOptions) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phase, setPhase] = useState<AnimationPhase>("visible");
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const rotate = useCallback(() => {
    // Phase 1: exit current word
    setPhase("exiting");

    timeoutRef.current = setTimeout(() => {
      // Phase 2: swap word and enter
      setCurrentIndex((prev) => (prev + 1) % words.length);
      setPhase("entering");

      timeoutRef.current = setTimeout(() => {
        // Phase 3: fully visible
        setPhase("visible");
      }, animationDuration);
    }, animationDuration);
  }, [words.length, animationDuration]);

  useEffect(() => {
    // Respect prefers-reduced-motion: keep first word static, no timers, no animation
    if (typeof window !== "undefined") {
      const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
      if (mq.matches) {
        setPhase("visible");
        return;
      }
    }

    const timer = setInterval(rotate, interval);
    return () => {
      clearInterval(timer);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [rotate, interval]);

  return {
    currentWord: words[currentIndex],
    phase,
    currentIndex,
  };
};